import { Component, type ErrorInfo, type ReactNode } from 'react'
import { logError } from '../lib/logger'

interface Props {
  children: ReactNode
}
interface State {
  error: Error | null
}

/**
 * Last-resort guard so a render/mount failure shows a readable message instead of
 * a blank white screen. Placed OUTSIDE the router so it also catches a router
 * init failure (e.g. a browser extension that has tampered with window.history /
 * window.location, a common cause of "works for me, blank for them").
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logError('render crash', error)
    // Surface the component stack in dev so the cause is visible in the console.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[share] component stack', info.componentStack)
    }
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <main
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          fontFamily: 'system-ui, sans-serif',
          color: '#1a1a1a',
          background: '#f5f3f0',
        }}
      >
        <div style={{ maxWidth: 440, textAlign: 'center' }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>
            This page could not load
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.5, margin: '0 0 12px' }}>
            Please refresh. If it keeps happening, a browser extension may be
            interfering with this page. Try an incognito window with extensions
            disabled, or a different browser.
          </p>
          <pre
            style={{
              fontSize: 12,
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: 'rgba(0,0,0,0.05)',
              borderRadius: 8,
              padding: 12,
              margin: 0,
            }}
          >
            {error.message}
          </pre>
        </div>
      </main>
    )
  }
}
